/**
 * Choosing which minute of a long film opens the book or the capsule.
 *
 * A creator who shot a two-minute introduction used to be told to go and cut it
 * somewhere else: the studio refused anything over the cover's length, which is a
 * refusal nobody can act on with the tool in front of them. Here the film stays
 * whole and the creator picks the window — the opening frame is the one they
 * choose, and the cover plays exactly that window.
 *
 * The screen they are given is the one every editor has taught them to expect:
 * **the film above, and the film's own timeline below**, so the thing being
 * chosen is visible in both. Three things make that a choice rather than a guess:
 *
 *  - the **strip is the film's frames**, evenly spaced across its whole length.
 *    A bar with a thumb on it says where in the timeline you are; a strip of
 *    pictures says *which part of the film you are keeping*, which is the only
 *    question being asked here. It is why the control stopped being a slider.
 *  - the **strip is the travel**, not the film: the window that slides over it
 *    is always a full minute wide, so the handles mark the minute being kept and
 *    the ends of the track are the earliest and latest minutes there are. What is
 *    outside the window is masked rather than merely tinted, the way the audio
 *    desk masks the parts of a waveform it is cutting.
 *  - the **preview holds still while a minute is dragged**, and shows the frame
 *    that minute opens on. A film that plays on under the finger turns the one
 *    decision this screen exists for into a moving target.
 *
 * The preview arrives **silent**, with a button to change that. An editor that
 * starts talking the moment it opens is a room-filling surprise, it is blocked
 * outright by a browser until somebody has tapped something, and it is how a
 * creator ends up hearing their own film after they have closed the studio.
 */
import {useState} from 'react'
import {
  Image,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  COVER_VIDEO_MAX_SECONDS,
  coverFractionFromStart,
  coverMaxStartMs,
  coverStartFromFraction,
  coverWindow,
} from '#/lib/coverTrim'
import {formatDuration} from '#/lib/flashbookAudioEditor'
import {web} from '#/alf'
import {StudioWindowVideo} from '#/components/studio/StudioWindowVideo'
import {useFilmFrames} from '#/components/studio/useFilmFrames'
import {useTrackScrub} from '#/components/studio/useTrackScrub'
import {OVER_ART} from '#/themes-studio/overArt'
import {useStudioChrome} from '#/themes-studio/useChrome'

export type CoverFilm = {
  uri: string
  /** Missing when the platform never measured the film; then there is no trim. */
  durationMs?: number
}

export function StudioCoverTrimSheet({
  film,
  startMs: initialStartMs = 0,
  accent,
  /** How much of the film the cover may run — 60s for a book, 180s for a capsule. */
  windowSeconds = COVER_VIDEO_MAX_SECONDS,
  onApply,
  onCancel,
}: {
  film: CoverFilm
  /** The window already chosen, so re-opening the trimmer continues from it. */
  startMs?: number
  accent: string
  windowSeconds?: number
  onApply: (startMs: number) => void
  onCancel: () => void
}) {
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const durationMs = film.durationMs ?? 0
  const windowMs = windowSeconds * 1000
  const [startMs, setStartMs] = useState(
    () => coverWindow(durationMs, initialStartMs, windowMs).startMs,
  )
  /** Playing as soon as it opens, silently: a still frame says less than motion. */
  const [playing, setPlaying] = useState(true)
  const [soundOn, setSoundOn] = useState(false)
  const [dragging, setDragging] = useState(false)

  const window = coverWindow(durationMs, startMs, windowMs)
  const travel = coverMaxStartMs(durationMs, windowMs)
  const total = Math.max(durationMs, 1)
  const keptLeft = (window.startMs / total) * 100
  const keptWidth = ((window.endMs - window.startMs) / total) * 100
  const frames = useFilmFrames(film.uri, durationMs)

  const {onTrackLayout, panHandlers, touchAxis} = useTrackScrub({
    value: coverFractionFromStart(startMs, durationMs, windowMs),
    onChange: next =>
      setStartMs(coverStartFromFraction(next, durationMs, windowMs)),
    onDragStart: () => setDragging(true),
    onDragEnd: () => setDragging(false),
  })

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onCancel}>
      <View
        style={[
          styles.scrim,
          styles.scrimWeb,
          {backgroundColor: chrome.scrim},
        ]}>
        <View
          testID="studioCoverTrimSheet"
          style={[
            styles.panel,
            {backgroundColor: chrome.fill, borderColor: chrome.border},
          ]}>
          <View style={styles.header}>
            <View
              style={[
                styles.badge,
                {
                  backgroundColor: chrome.accentTint,
                  borderColor: chrome.accentBorder,
                },
              ]}>
              <Feather name="scissors" size={11} color={accent} />
            </View>
            <Text style={[styles.title, {color: chrome.text}]}>
              <Trans>Trim the cover</Trans>
            </Text>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Keep the film as it is`}
              accessibilityHint={l`Leaves the cover unchanged`}
              onPress={onCancel}
              testID="studioCoverTrimCancel"
              hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
              style={styles.closeBtn}>
              <Feather name="x" size={16} color={chrome.textMuted} />
            </TouchableOpacity>
          </View>

          <View style={styles.stage}>
            <StudioWindowVideo
              uri={film.uri}
              startMs={window.startMs}
              windowMs={windowMs}
              /*
               * Held still under a finger, and looping the minute being kept: the
               * creator is looking at the seconds the learner will get, over and
               * over, until they are the right seconds.
               */
              playing={playing && !dragging}
              muted={!soundOn}
              loopWindow
              showControls={false}
              style={StyleSheet.absoluteFill}
            />

            {/*
              The transport floats *over the film*, so it cannot be drawn in the
              studio's own ink — the pixels behind it are a photograph of anything.
              `canvas` is the chrome's family for exactly this: a control on the
              book canvas that stays legible whatever it lands on.
            */}
            <View style={styles.transport}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={
                  playing ? l`Pause the film` : l`Play the film`
                }
                accessibilityHint={l`Previews the minute you have chosen`}
                onPress={() => setPlaying(p => !p)}
                testID="studioCoverTrimPlay"
                style={[
                  styles.transportBtn,
                  {
                    backgroundColor: chrome.canvas,
                    borderColor: chrome.canvasBorder,
                  },
                ]}>
                <Feather
                  name={playing ? 'pause' : 'play'}
                  size={15}
                  color={chrome.onCanvas}
                />
              </TouchableOpacity>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={
                  soundOn ? l`Mute the film` : l`Hear the film`
                }
                accessibilityHint={l`The preview starts silent`}
                onPress={() => setSoundOn(s => !s)}
                testID="studioCoverTrimSound"
                style={[
                  styles.transportBtn,
                  {
                    backgroundColor: chrome.canvas,
                    borderColor: chrome.canvasBorder,
                  },
                ]}>
                <Feather
                  name={soundOn ? 'volume-2' : 'volume-x'}
                  size={15}
                  // The quieter ink is the honest state for a film that is not
                  // making a sound: the button shows what you are *not* hearing.
                  color={soundOn ? chrome.onCanvas : chrome.onCanvasMuted}
                />
              </TouchableOpacity>
            </View>
          </View>

          <View
            accessibilityRole="adjustable"
            accessibilityLabel={l`Where the cover starts`}
            accessibilityHint={l`Drag left and right to move the minute it opens on`}
            /*
             * A range and nothing else: the readout underneath already says which
             * minute this is in words, and a value description carrying both a
             * range and a phrase is one a screen reader cannot read out.
             */
            accessibilityValue={{min: 0, max: travel, now: window.startMs}}
            onLayout={onTrackLayout}
            testID="studioCoverTrimBar"
            style={[styles.strip, touchAxis]}
            {...panHandlers}>
            {frames.map((frame, index) => (
              <View
                key={index}
                testID="studioCoverTrimFrame"
                style={styles.cell}>
                {frame ? (
                  <Image
                    source={{uri: frame}}
                    style={StyleSheet.absoluteFill}
                    resizeMode="cover"
                    accessibilityIgnoresInvertColors
                    // Decoration for the strip: the strip's own label is the
                    // control, and a picture of a frame is not a thing to read.
                    accessible={false}
                  />
                ) : (
                  <View
                    style={[
                      StyleSheet.absoluteFill,
                      {backgroundColor: chrome.fillStrong},
                    ]}
                  />
                )}
              </View>
            ))}

            {keptLeft > 0 && (
              <View
                style={[
                  styles.masked,
                  {
                    left: 0,
                    width: `${keptLeft}%`,
                    backgroundColor: OVER_ART.trimVeil,
                  },
                ]}
              />
            )}
            {keptLeft + keptWidth < 100 && (
              <View
                style={[
                  styles.masked,
                  {
                    left: `${keptLeft + keptWidth}%`,
                    right: 0,
                    backgroundColor: OVER_ART.trimVeil,
                  },
                ]}
              />
            )}

            <View
              testID="studioCoverTrimKept"
              pointerEvents="none"
              style={[
                styles.kept,
                {
                  left: `${keptLeft}%`,
                  width: `${keptWidth}%`,
                  borderColor: accent,
                  backgroundColor: OVER_ART.trimLift,
                },
              ]}
            />

            {/*
              Two handles, one window. They move together, because the minute is a
              length the product fixes rather than a range a creator resizes — and
              drawing only one of them would say the other edge is movable, which
              is the lie a single thumb on a bar tells.
            */}
            <View
              testID="studioCoverTrimHandleStart"
              pointerEvents="none"
              style={[
                styles.handle,
                {left: `${keptLeft}%`, backgroundColor: accent},
              ]}>
              <View style={styles.grip} />
            </View>
            <View
              testID="studioCoverTrimHandleEnd"
              pointerEvents="none"
              style={[
                styles.handle,
                styles.handleEnd,
                {left: `${keptLeft + keptWidth}%`, backgroundColor: accent},
              ]}>
              <View style={styles.grip} />
            </View>
          </View>

          <View style={styles.legend}>
            <Text
              testID="studioCoverTrimRange"
              style={[styles.legendStrong, {color: chrome.text}]}>
              {`${timeLabel(window.startMs)} – ${timeLabel(window.endMs)}`}
            </Text>
            <Text style={[styles.legendMeta, {color: chrome.textFaint}]}>
              {l`of ${timeLabel(durationMs)}`}
            </Text>
          </View>

          <Text style={[styles.hint, {color: chrome.textMuted}]}>
            {l`Drag to slide the minute. The cover opens on its first frame, and plays for ${windowSeconds} seconds.`}
          </Text>

          <View style={styles.actions}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Pick a different film`}
              accessibilityHint={l`Returns to the studio without changing the cover`}
              onPress={onCancel}
              style={[styles.ghost, {borderColor: chrome.borderStrong}]}>
              <Text style={[styles.ghostTxt, {color: chrome.textMuted}]}>
                {l`Cancel`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Use this minute as the cover`}
              accessibilityHint={l`Plays these ${windowSeconds} seconds`}
              onPress={() => onApply(window.startMs)}
              testID="studioCoverTrimApply"
              style={[styles.apply, {backgroundColor: accent}]}>
              <Feather name="check" size={15} color={chrome.onAccent} />
              <Text style={[styles.applyTxt, {color: chrome.onAccent}]}>
                {l`Use these ${windowSeconds} seconds`}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  )
}

/** `1:05`, on the app's existing formatter so every duration reads alike. */
function timeLabel(ms: number): string {
  return formatDuration(Math.max(0, Math.round(ms)) / 1000)
}

const HANDLE_WIDTH = 14

const styles = StyleSheet.create({
  /*
   * A sheet on a phone, a card on a screen with room for one.
   *
   * The sheet arrives from the bottom edge on a device, where the film and the
   * strip are the whole of the screen anyway. On the web it used to stretch to
   * whatever width the window happened to be — a 16:9 preview and a *Use these
   * 60 seconds* button set 1800px wide, under a studio that is itself framed to
   * the shell's centre column. A media editor is a thing you look at, not a field
   * you fill in, so past a phone's width it becomes a centred card of one size,
   * which is also how the app's own dialogs present themselves.
   */
  scrim: {flex: 1, justifyContent: 'flex-end'},
  panel: {
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    borderWidth: 1,
    padding: 16,
    gap: 12,
    ...web({
      width: '100%',
      maxWidth: 560,
      alignSelf: 'center',
      borderRadius: 22,
    }),
  },
  /* The card is centred on the web, so the scrim has to stop pinning it down. */
  scrimWeb: web({justifyContent: 'center', padding: 20}),
  header: {flexDirection: 'row', alignItems: 'center', gap: 9},
  badge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {flex: 1, fontSize: 16, fontWeight: '800', letterSpacing: -0.2},
  closeBtn: {padding: 2},

  /* The preview: the film, and the two controls that float on it. */
  stage: {
    aspectRatio: 16 / 9,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: OVER_ART.videoMatte,
  },
  transport: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    flexDirection: 'row',
    gap: 7,
  },
  transportBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  transportIcon: {marginLeft: 1},

  /* The strip: one cell per frame, and a window that slides over them. */
  strip: {
    height: 62,
    flexDirection: 'row',
    borderRadius: 10,
    overflow: 'hidden',
    position: 'relative',
    /* One hairline between frames, so the strip reads as film rather than as a
     * blurred panorama: eight pictures butted together are eight pictures nobody
     * can tell apart, which is the state this control replaced. */
    gap: 1,
    backgroundColor: OVER_ART.trimSeam,
  },
  cell: {flex: 1, overflow: 'hidden'},
  masked: {position: 'absolute', top: 0, bottom: 0},
  kept: {position: 'absolute', top: 0, bottom: 0, borderWidth: 2.5},
  handle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: HANDLE_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /*
   * Inside the window they bound, one bar at each end, the way every editor draws
   * a clip's edge: the start bar grows rightwards from the opening frame and the
   * end bar leftwards from the last one, so the two of them *are* the minute and
   * nothing outside them is kept.
   */
  handleEnd: {marginLeft: -HANDLE_WIDTH},
  grip: {
    width: 2,
    height: 22,
    borderRadius: 1,
    backgroundColor: OVER_ART.inkStrong,
  },

  legend: {flexDirection: 'row', alignItems: 'baseline', gap: 6, marginTop: -2},
  legendStrong: {
    fontSize: 17,
    fontWeight: '800',
    letterSpacing: -0.3,
    fontVariant: ['tabular-nums'],
  },
  legendMeta: {fontSize: 11.5, fontWeight: '600'},
  hint: {fontSize: 11.5, lineHeight: 16, fontWeight: '600', marginTop: -6},

  actions: {flexDirection: 'row', alignItems: 'center', gap: 8},
  ghost: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  ghostTxt: {fontSize: 13, fontWeight: '700'},
  apply: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 12,
    paddingVertical: 12,
  },
  applyTxt: {fontSize: 13.5, fontWeight: '800'},
})
